import { Box, Button, FormControlLabel, IconButton, Switch, TextField, Typography } from "@mui/material";
import { SettingDefinition } from "../../../service/model/setting-definition.model";
import { Setting } from "../../../service/model/setting.model";
import React, { useState } from "react";
import { CancelIcon, EditIcon, SaveIcon } from "../../../shared/icons";

export type InputType = 'number' | 'string' | 'boolean' | 'text';
interface SettingRowProps {
    definition: SettingDefinition;
    setting?: Setting;
    onSave: (name: string, value: any) => void;
  }
  
  const SettingRow: React.FC<SettingRowProps> = (props) => {
    const [editing, setEditing] = useState(false);
    const [inputValue, setInputValue] = useState<string|number|boolean>(props.setting?.value ?? props.definition.defaultValue ?? '');
    const [error, setError] = useState('');
  
    const validate = (val: any): boolean => {
      if (props.definition.required && (val === '' || val === null || val === undefined)) {
        setError(`${props.definition.label} is required`);
        return false;
      }
  
      if (props.definition.inputType === 'number') {
        const num = parseFloat(val);
        if (props.definition.minValue != null && num < props.definition.minValue) {
          setError(`Minimum value is ${props.definition.minValue}`);
          return false;
        }
        if (props.definition.maxValue != null && num > props.definition.maxValue) {
          setError(`Maximum value is ${props.definition.maxValue}`);
          return false;
        }
      }
  
      if (['string', 'text'].includes(props.definition.inputType)) {
        if (props.definition.minLength != null && val.length < props.definition.minLength) {
          setError(`Minimum length is ${props.definition.minLength}`);
          return false;
        }
        if (props.definition.maxLength != null && val.length > props.definition.maxLength) {
          setError(`Maximum length is ${props.definition.maxLength}`);
          return false;
        }
        if (props.definition.pattern && !(new RegExp(props.definition.pattern).test(val))) {
          setError(`Invalid format`);
          return false;
        }
      }
  
      setError('');
      return true;
    };
  
    const handleSave = () => {
      if (!validate(inputValue)) return;
      props.onSave(props.definition.name, inputValue);
      setEditing(false);
    };
  
    const handleCancel = () => {
      setInputValue(props.setting?.value ?? props.definition.defaultValue ?? '');
      setError('');
      setEditing(false);
    };
  
    const renderInput = () => {
      switch (props.definition.inputType) {
        case 'boolean':
          return (
            <FormControlLabel
              control={
                <Switch
                  checked={!!inputValue}
                  onChange={(e) => setInputValue(e.target.checked)}
                />
              }
              label={props.definition.label}
            />
          );
        case 'text':
          return (
            <TextField
              multiline
              fullWidth
              minRows={3}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              error={!!error}
              helperText={error}
            />
          );
        case 'number':
          return (
            <TextField
              type="number"
              fullWidth
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              error={!!error}
              helperText={error}
            />
          );
        default:
          return (
            <TextField
              fullWidth
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              error={!!error}
              helperText={error}
            />
          );
      }
    };
  
    return (
      <Box mb={3} display="flex" alignItems="start" justifyContent="space-between" gap={2} sx={{bgcolor: "#ffffff", p: 2, borderRadius: 4}}>
        <Box flex={1}>
          <Typography variant="subtitle2">{props.definition.label}</Typography>
          {props.definition.description && (
            <Typography variant="body2" color="text.secondary">
              {props.definition.description}
            </Typography>
          )}
  
          {!editing ? (
            <Typography variant="body1" mt={1}>
              {props.definition.inputType === 'boolean' ? (props.setting?.value ? 'Yes' : 'No') : props.setting?.value ?? props.definition.defaultValue ?? '-'}
            </Typography>
          ) : (
            <>
              {renderInput()}
              <Box mt={1} display="flex" gap={1}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  size="small"
                  startIcon={<SaveIcon />}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  size="small"
                  startIcon={<CancelIcon />}
                >
                  Cancel
                </Button>
              </Box>
            </>
          )}
        </Box>
  
        {!editing && (
          <IconButton onClick={() => setEditing(true)}>
            <EditIcon color="primary"/>
          </IconButton>
        )}
      </Box>
    );
  };
  
  export default SettingRow;